import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Coins, Loader2, Sparkles, ShoppingCart, Info, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { creditsApi } from "@/lib/api";
import { BuyCreditsModal } from "@/components/app/BuyCreditsModal";

const CreditsContext = createContext(null);
const EXPLAINER_KEY = "promptivo.aiCreditsExplainerSeen";

export function CreditsProvider({ children }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [prices, setPrices] = useState({});
  const [buyOpen, setBuyOpen] = useState(false);

  // Shared credit-confirm modal state.
  const [confirm, setConfirm] = useState(null); // { quote, run, phase }
  const [explainer, setExplainer] = useState(false);
  const resolveRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const d = await creditsApi.balance();
      setBalance(d.balance);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refresh();
      creditsApi.actionPrices().then((d) => setPrices(d.prices || {})).catch(() => {});
    } else {
      setBalance(null);
    }
  }, [user, refresh]);

  const openBuy = useCallback(() => setBuyOpen(true), []);
  const priceOf = useCallback((actionKey) => prices[actionKey], [prices]);

  // Fetch quote → show confirm modal → run the provided async fn. Resolves with the result or null.
  const confirmAndRun = useCallback((actionKey, run) => {
    return new Promise(async (resolve) => {
      resolveRef.current = resolve;
      let quote;
      try {
        quote = await creditsApi.quote(actionKey);
      } catch {
        toast.error("Nie udało się pobrać wyceny.");
        resolve(null);
        return;
      }
      try {
        setExplainer(!localStorage.getItem(EXPLAINER_KEY));
      } catch {
        setExplainer(false);
      }
      setConfirm({ quote, run, phase: quote.sufficient ? "confirm" : "insufficient" });
    });
  }, []);

  const finish = useCallback((value) => {
    setConfirm(null);
    if (resolveRef.current) {
      resolveRef.current(value);
      resolveRef.current = null;
    }
  }, []);

  const doConfirm = useCallback(async () => {
    try {
      localStorage.setItem(EXPLAINER_KEY, "1");
    } catch {
      /* ignore */
    }
    setExplainer(false);
    setConfirm((c) => ({ ...c, phase: "running" }));
    try {
      const result = await confirm.run();
      await refresh();
      const cost = result?.cost;
      if (typeof cost === "number") {
        toast.success(cost > 0 ? `Gotowe. Pobrano ${cost} kr.` : "Gotowe — pierwszy raz za darmo.");
      }
      finish(result ?? true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402 && detail?.code === "INSUFFICIENT_CREDITS") {
        setConfirm((c) => ({
          ...c,
          phase: "insufficient",
          quote: { ...c.quote, sufficient: false, cost: detail.cost, balance: detail.balance, recommendedPack: detail.recommendedPack },
        }));
        return;
      }
      if (status === 502) {
        toast.error(typeof detail === "string" ? detail : "Operacja nie powiodła się — kredyty zwrócone.");
        await refresh();
        finish(null);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Operacja nie powiodła się.");
      setConfirm((c) => ({ ...c, phase: "confirm" }));
    }
  }, [confirm, refresh, finish]);

  const q = confirm?.quote;
  const phase = confirm?.phase;

  return (
    <CreditsContext.Provider value={{ balance, refresh, openBuy, priceOf, confirmAndRun }}>
      {children}
      <BuyCreditsModal open={buyOpen} onClose={() => setBuyOpen(false)} />

      {confirm && (
        <div
          className="pointer-events-auto fixed inset-0 z-[130] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
          onClick={() => phase !== "running" && finish(null)}
          data-testid="credit-confirm-modal"
        >
          <div className="w-full max-w-md rounded-t-panel border border-brand-line bg-brand-surface p-6 sm:rounded-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-brand-text">
                {phase === "insufficient" ? "Za mało kredytów" : q?.label}
              </h2>
              {phase !== "running" && (
                <button type="button" onClick={() => finish(null)} className="flex h-8 w-8 items-center justify-center rounded-field text-brand-sec hover:bg-brand-elevated">
                  <X size={17} />
                </button>
              )}
            </div>

            {explainer && phase === "confirm" && (
              <div className="mb-4 flex items-start gap-2 rounded-field bg-brand-elevated px-3 py-2.5 text-xs text-brand-sec">
                <Info size={14} className="mt-0.5 shrink-0 text-brand-accent" />
                <span>Funkcje AI kosztują kredyty. Płacisz tylko za wykonaną operację — przy błędzie kredyty wracają. Kopiowanie, zapis i pobieranie są bezpłatne.</span>
              </div>
            )}

            {phase === "insufficient" ? (
              <div data-testid="credit-confirm-insufficient">
                <p className="text-sm text-brand-sec">
                  Ta operacja kosztuje <strong className="text-brand-text">{q?.cost} kr.</strong>, a Twoje saldo to{" "}
                  <strong className="text-brand-text">{q?.balance ?? balance} kr.</strong>
                </p>
                {q?.recommendedPack && (
                  <div className="mt-4 rounded-field border border-brand-line bg-brand-bg p-4">
                    <p className="text-xs uppercase tracking-wide text-brand-muted">Wystarczy najmniejszy pakiet</p>
                    <p className="mt-1 font-display text-base font-semibold text-brand-text">
                      {q.recommendedPack.name} — {q.recommendedPack.creditsTotal} kr. za {q.recommendedPack.grossPrice} {q.recommendedPack.currency}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="credit-confirm-topup"
                  onClick={() => { finish(null); openBuy(); }}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on"
                >
                  <ShoppingCart size={16} /> Dokup kredyty
                </button>
              </div>
            ) : (
              <>
                {q?.firstFree && (
                  <div className="mb-4 flex items-center gap-2 rounded-field border border-brand-accent/40 bg-brand-accent/10 px-3 py-2.5 text-sm text-brand-text" data-testid="credit-confirm-firstfree">
                    <Gift size={15} className="text-brand-accent" /> Pierwszy raz na koncie — <strong>za darmo</strong>.
                  </div>
                )}
                <div className="space-y-2 rounded-field border border-brand-line bg-brand-bg p-4 text-sm">
                  <Row label="Operacja" value={q?.label} />
                  <Row label="Koszt" value={<span className="inline-flex items-center gap-1 text-brand-accent"><Coins size={13} /> {q?.cost === 0 ? "0 kr. (za darmo)" : `${q?.cost} kr.`}</span>} />
                  <Row label="Saldo teraz" value={`${q?.balance} kr.`} />
                  <Row label="Saldo po wykonaniu" value={`${q?.balanceAfter} kr.`} bold />
                </div>
                <button
                  type="button"
                  data-testid="credit-confirm-run"
                  onClick={doConfirm}
                  disabled={phase === "running"}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {phase === "running" ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={16} />}
                  {phase === "running" ? "Pracuję…" : "Uruchom"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </CreditsContext.Provider>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-brand-sec">{label}</span>
      <span className={bold ? "font-semibold text-brand-text" : "text-brand-text"}>{value}</span>
    </div>
  );
}

export function useCredits() {
  return useContext(CreditsContext) || { balance: null, refresh: () => {}, openBuy: () => {}, priceOf: () => undefined, confirmAndRun: (_k, run) => run() };
}
